import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import UserGroupContext from '@atom/components/userGroupDetail/UserGroupContext';
import { UPDATE_USER_GROUP } from '@atom/graph/userGroup';
import {
  Button,
  Icon,
  IconButton,
  Progress,
  Snackbar,
  TextField,
} from '@atom/mui';
import { UserGroup, UserGroupUpdateInput } from '@atom/types/userGroups';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import classes from './userGroupInfo.module.css';

const DetailsSection = () => {
  const { userGroup } = useContext(UserGroupContext);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [baseLocation, setBaseLocation] = useState<string>(
    userGroup.baseLocation,
  );
  const [organization, setOrganization] = useState<string>(
    userGroup.organization,
  );
  const [appropriation, setAppropriation] = useState<string>(
    userGroup.appropriation,
  );

  const [updateUserGroup, { loading }] = useMutation<
    { userGroupUpdate: UserGroup },
    { input: UserGroupUpdateInput }
  >(UPDATE_USER_GROUP);

  const confirm = async () => {
    try {
      await updateUserGroup({
        variables: {
          input: {
            id: userGroup.id,
            baseLocation,
            organization,
            appropriation,
          },
        },
      });

      setEditMode(false);
    } catch (error) {
      if (error?.networkError?.statusCode === 422) {
        Snackbar.error({
          message:
            'The user has unapproved time entries for the group you wish to remove',
        });
      }

      setEditMode(false);
    }
  };

  useEffect(() => {
    if (!editMode) {
      setBaseLocation(userGroup.baseLocation);
      setOrganization(userGroup.organization);
      setAppropriation(userGroup.appropriation);
    }
  }, [editMode]);

  const onCancel = () => {
    setEditMode(false);
  };

  const rightButtons = editMode ? (
    <div>
      <Button onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button color="primary" onClick={confirm} disabled={loading}>
        Save
      </Button>
      {loading && (
        <div className={classes.spinnerContainer}>
          <Progress size={25} thickness={2} />
        </div>
      )}
    </div>
  ) : hasRolePermissions(ROLE_SETS.ORG_ADMIN) ? (
    <IconButton onClick={() => setEditMode(true)}>
      <Icon>edit</Icon>
    </IconButton>
  ) : (
    <div />
  );

  return (
    <>
      <div className={classes.titleContainer}>
        <div>Details</div>
        {rightButtons}
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.listRow}>
          <div className={clsx(classes.label, classes.edit)}>Created Date:</div>
          <div className={classes.value}>
            {attributeDisplayUtility.display(userGroup.createdDate, 'date')}
          </div>
        </div>
        <div className={classes.listRow}>
          <div className={clsx(classes.label, classes.edit)}>Created By:</div>
          <div className={classes.value}>{`${R.pathOr(
            '',
            ['createdBy', 'firstName'],
            userGroup,
          )} ${R.pathOr('', ['createdBy', 'lastName'], userGroup)}`}</div>
        </div>
        <div className={classes.listRow}>
          <div className={clsx(classes.label, classes.edit)}>
            Base Location:
          </div>
          <div className={classes.value}>
            {editMode ? (
              <TextField
                value={baseLocation}
                onChange={event => setBaseLocation(event.target.value)}
              />
            ) : (
              userGroup.baseLocation
            )}
          </div>
        </div>
        <div className={classes.listRow}>
          <div className={clsx(classes.label, classes.edit)}>Organization:</div>
          <div className={classes.value}>
            {editMode ? (
              <TextField
                value={organization}
                onChange={event => setOrganization(event.target.value)}
              />
            ) : (
              userGroup.organization
            )}
          </div>
        </div>
        <div className={classes.listRow}>
          <div className={clsx(classes.label, classes.edit)}>
            Appropriation:
          </div>
          <div className={classes.value}>
            {editMode ? (
              <TextField
                value={appropriation}
                onChange={event => setAppropriation(event.target.value)}
              />
            ) : (
              userGroup.appropriation
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailsSection;
